import { createContext, useState, useEffect } from 'react';

import axios, { AxiosError } from 'axios';

import { useToken } from 'src/features/auth';
import { useGetParkingStatus } from 'src/features/parkingManagement/api/useGetParkingStatus';
import { useParking } from 'src/providers/ParkingProvider';

interface ParkingStatusContextValue {
    available?: boolean;
    refetch: () => Promise<void>;
    isFetching: boolean;
    isConnectionError: boolean;
}

const ParkingStatusContext = createContext<ParkingStatusContextValue | undefined>(undefined);

export const ParkingStatusProvider = ({ children }: { children: React.ReactNode }) => {
    const { parkingId } = useParking();
    const { data: tokenData, isPending: isTokenPending } = useToken();
    const query = useGetParkingStatus({
        parkingId,
        config: {
            enabled: !!(parkingId && !isTokenPending && tokenData),
        },
    });

    const [isConnectionError, setIsConnectionError] = useState<boolean>(false);

    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => {
                setIsConnectionError(false);
                return response;
            },
            (err) => {
                const error = err as AxiosError<{ code: string; message: string }>;
                // check if error matches offline API response
                if (
                    error.response?.data?.code === 'smartpark_connection_error' ||
                    error.message === 'Something went wrong when trying to reach the Parking'
                ) {
                    setIsConnectionError(true);
                }
                return Promise.reject(error);
            },
        );

        return () => axios.interceptors.response.eject(interceptor);
    }, []);

    let available = !!query.data?.available;
    if (!parkingId || !tokenData || query.isLoading) {
        available = true;
    }

    return (
        <ParkingStatusContext.Provider
            value={{
                available,
                refetch: async () => {
                    await query.refetch();
                },
                isFetching: query.isFetching,
                isConnectionError,
            }}
        >
            {children}
        </ParkingStatusContext.Provider>
    );
};

export default ParkingStatusContext;
